import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import HandymanRoundedIcon from "@mui/icons-material/HandymanRounded";
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";

/**
 * =============================================================================
 *  CATEGORIAS DOS SISTEMAS — agrupamento da home
 * =============================================================================
 *
 *  A tabela SISTEMA não tem coluna de categoria, então o agrupamento vem deste
 *  mapa. `resolverCategoria` já tenta ler `sistema.categoria` ANTES do mapa —
 *  ou seja, no dia em que a coluna existir no banco, é só passar a mandá-la na
 *  API que este arquivo vira apenas o fallback, sem mexer em tela nenhuma.
 *
 *  Sistema novo que não estiver aqui cai em "Outros" e continua aparecendo
 *  normalmente — nunca some da home.
 *
 *  Para categorizar um sistema novo: acrescente uma linha em MAPA_CATEGORIAS
 *  com um pedaço do título (sem acento, em CAIXA ALTA).
 * =============================================================================
 */

export const CATEGORIA_OUTROS = "Outros";

/**
 * Ordem em que as seções aparecem na home. Segue o caminho do dinheiro na
 * prefeitura: o que ENTRA (tributos), o que SAI (compras -> contabilidade),
 * a gestão interna, o atendimento ao público e, por fim, as ferramentas.
 */
export const CATEGORIAS = [
    {
        nome: "Tributos e Arrecadação",
        descricao: "Impostos, guias, notas e cobranças",
        icone: ReceiptLongRoundedIcon,
        hue: 152, // verde
    },
    {
        nome: "Compras e Licitações",
        descricao: "Do pedido da secretaria ao contrato assinado",
        icone: ShoppingCartRoundedIcon,
        hue: 28, // laranja
    },
    {
        nome: "Contabilidade e Transparência",
        descricao: "Execução orçamentária, convênios e prestação de contas",
        icone: AccountBalanceRoundedIcon,
        hue: 188, // azul-petróleo
    },
    {
        nome: "Administração e Gestão",
        descricao: "Servidores, protocolo e relatórios internos",
        icone: BusinessCenterRoundedIcon,
        hue: 265, // roxo
    },
    {
        nome: "Atendimento ao Cidadão",
        descricao: "Serviços de porta aberta ao público",
        icone: PeopleAltRoundedIcon,
        hue: 214, // azul
    },
    {
        nome: "Ferramentas",
        descricao: "Recursos de apoio usados pelos demais sistemas",
        icone: HandymanRoundedIcon,
        hue: 330, // rosa
    },
    {
        nome: CATEGORIA_OUTROS,
        descricao: "Ainda sem categoria definida",
        icone: AppsRoundedIcon,
        hue: 210, // cinza-azulado
    },
];

/**
 * Trecho do título (sem acento, CAIXA ALTA) -> categoria.
 * A busca é por SUBSTRING, então "COBRANCA DE AGUA" casa com o título real
 * "Cobrança de Água Municipal" e aguenta renome com sufixo.
 *
 * A ORDEM DAS LINHAS É A ORDEM DOS CARDS dentro da seção (fluxo de trabalho,
 * não alfabética). Sistema que não está aqui vai para o fim da sua seção.
 */
const MAPA_CATEGORIAS: Array<[string, string]> = [
    // --- Tributos e Arrecadação: o sistema-mãe, os impostos, as notas, as taxas ---
    ["ARRECADACAO", "Tributos e Arrecadação"],
    ["IPTU", "Tributos e Arrecadação"],
    ["ITBI", "Tributos e Arrecadação"],
    ["ISSQN", "Tributos e Arrecadação"],
    ["NFS-E", "Tributos e Arrecadação"],
    ["NOTAS FISCAIS", "Tributos e Arrecadação"],
    ["COBRANCA DE AGUA", "Tributos e Arrecadação"],

    // --- Compras e Licitações: na ordem em que o processo anda ---
    ["SOLICITACAO DE COMPRA", "Compras e Licitações"],
    ["PROCESSO LICITATORIO", "Compras e Licitações"],
    ["LICITACOES", "Compras e Licitações"],
    ["CADASTRO DE PRODUTOS", "Compras e Licitações"],

    // --- Contabilidade e Transparência ---
    ["SIAFIC", "Contabilidade e Transparência"],
    ["CONVENIOS", "Contabilidade e Transparência"],
    ["PORTAL DA TRANSPARENCIA", "Contabilidade e Transparência"],

    // --- Administração e Gestão ---
    ["PORTAL DO SERVIDOR", "Administração e Gestão"],
    ["PROTOCOLO", "Administração e Gestão"], // uso interno: comunicações e processos
    ["RELATORIOS DE GESTAO", "Administração e Gestão"],

    // --- Atendimento ao Cidadão ---
    // ATENÇÃO: use sempre o título CRU do banco. O "Cidade Digital" é exibido com
    // outro nome em alguns municípios (meuMax, Trindade na Palma da Mão,
    // TRESPADIGITAL) — ver resolverSistemaDisplay —, mas o título continua este.
    ["CIDADE DIGITAL", "Atendimento ao Cidadão"],
    ["CADASTRO UNICO", "Atendimento ao Cidadão"],

    // --- Ferramentas ---
    ["ASSINATURA DIGITAL", "Ferramentas"],
];

/** Tira acento e caixa, pra comparação de título ser tolerante. */
function normalizar(texto: any): string {
    return String(texto ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
        .trim();
}

/**
 * Descobre a categoria de um sistema.
 * Prioridade: campo vindo da API -> mapa por título -> "Outros".
 */
export function resolverCategoria(sistema: any): string {
    const daApi = String(sistema?.categoria ?? "").trim();
    if (daApi) return daApi;

    const indice = indiceNoMapa(sistema);
    return indice >= 0 ? MAPA_CATEGORIAS[indice][1] : CATEGORIA_OUTROS;
}

/** Posição do sistema em MAPA_CATEGORIAS (-1 se não estiver lá). */
function indiceNoMapa(sistema: any): number {
    const titulo = normalizar(sistema?.titulo);
    return MAPA_CATEGORIAS.findIndex(([trecho]) => titulo.includes(normalizar(trecho)));
}

/** Ordena pela posição no mapa; quem não está no mapa vai pro fim, em ordem alfabética. */
function porOrdemDoMapa(a: any, b: any): number {
    const ia = indiceNoMapa(a);
    const ib = indiceNoMapa(b);
    const pa = ia < 0 ? Number.MAX_SAFE_INTEGER : ia;
    const pb = ib < 0 ? Number.MAX_SAFE_INTEGER : ib;

    if (pa !== pb) return pa - pb;
    return String(a?.titulo ?? "").localeCompare(String(b?.titulo ?? ""), "pt-BR");
}

/** Metadados da categoria (ícone, cor, descrição). Cai em "Outros" se não achar. */
export function metadadosCategoria(nome: string) {
    return (
        CATEGORIAS.find((c) => c.nome === nome) ??
        CATEGORIAS[CATEGORIAS.length - 1]
    );
}

/**
 * Agrupa os sistemas nas categorias, na ordem de CATEGORIAS.
 * Categorias sem nenhum sistema são omitidas.
 */
export function agruparPorCategoria<T>(sistemas: T[]): Array<{ categoria: string; sistemas: T[] }> {
    const grupos = new Map<string, T[]>();

    for (const sistema of sistemas) {
        const categoria = resolverCategoria(sistema);
        if (!grupos.has(categoria)) grupos.set(categoria, []);
        grupos.get(categoria)!.push(sistema);
    }

    const ordenadas = CATEGORIAS
        .map((c) => c.nome)
        .filter((nome) => grupos.has(nome));

    // categorias que vieram da API e não estão em CATEGORIAS entram no fim
    // (Array.from e não spread: o target do tsconfig é ES5 e não itera Map.keys())
    const extras = Array.from(grupos.keys()).filter(
        (nome) => !ordenadas.includes(nome)
    );

    return [...ordenadas, ...extras].map((categoria) => ({
        categoria,
        sistemas: grupos.get(categoria)!.slice().sort(porOrdemDoMapa),
    }));
}
